import { useCallback, useRef } from 'react';

/**
 * 防抖hook，用於延遲函數執行直到停止觸發一段時間後
 * @param callback 要執行的回調函數
 * @param delay 延遲時間（毫秒）
 * @returns 防抖後的函數
 */
export const useDebounce = <TArgs extends unknown[], TReturn = void>(
  callback: (...args: TArgs) => TReturn,
  delay: number,
): ((...args: TArgs) => void) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const debouncedFunction = useCallback(
    (...args: TArgs) => {
      // 清除之前的定時器
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // 設置新的定時器，延遲執行
      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    },
    [callback, delay],
  );

  return debouncedFunction;
};
